import React from 'react';
import { Button } from './ui/button';
import { heroContent, targetAudience, pricingContent } from '../data/mock';
import { ChevronRight, CheckCircle2, ExternalLink } from 'lucide-react';

const EEATO_GRAPH_URL = '/images/assets/8gr57j1y-eeato-graph.png';

const HeroSection = ({ onContactClick }) => {
  return (
    <section className="relative min-h-screen flex items-center overflow-hidden bg-gradient-to-b from-slate-950 via-slate-900 to-slate-800">
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 -left-20 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 -right-20 w-80 h-80 bg-teal-500/10 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-slate-800/50 rounded-full blur-3xl" />
        {/* Grid pattern */}
        <div 
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
            backgroundSize: '50px 50px'
          }}
        />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 md:pt-24 pb-12 md:pb-16">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left Content */}
          <div className="text-center lg:text-left">
            {/* Badge - Changed to pricing info */}
            <div className="inline-flex items-center gap-2 mb-8">
              <span className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse" />
              <span className="text-slate-300 text-sm font-medium">
                初期費用0円・月額0円
              </span>
            </div>

            {/* Main Copy */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
              <span className="md:hidden">E-E-A-T＋独自性を<br />数値で可視化</span>
              <span className="hidden md:inline">
                {heroContent.mainCopy.split('\n').map((line, i) => (
                  <span key={i}>
                    {line}
                    {i === 0 && <br />}
                  </span>
                ))}
              </span>
            </h1>

            {/* Sub Copy */}
            <p className="text-lg sm:text-xl text-slate-300 leading-relaxed mb-8 max-w-xl mx-auto lg:mx-0 whitespace-pre-line">
              {heroContent.subCopy}
            </p>

            {/* Target Audience */}
            <div className="mb-8 space-y-3">
              {targetAudience.slice(0, 2).map((audience, index) => (
                <div key={index} className="flex items-start gap-3 text-slate-400 justify-center lg:justify-start">
                  <CheckCircle2 className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                  <span className="text-sm">{audience}</span>
                </div>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-6">
              <Button
                onClick={onContactClick}
                size="lg"
                className="free_assessment_button bg-cyan-500 hover:bg-cyan-400 text-slate-900 font-semibold px-8 py-6 text-lg transition-all hover:shadow-lg hover:shadow-cyan-500/25 group"
              >
                {heroContent.ctaText}
                <ChevronRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button
                asChild
                variant="outline"
                size="lg"
                className="sample_report__button border-slate-600 text-slate-300 hover:bg-slate-800 hover:text-white px-8 py-6 text-lg"
              >
                <a href={pricingContent.sampleReportUrl} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="mr-2 w-5 h-5" />
                  サンプルレポートを見る
                </a>
              </Button>
            </div>

            {/* Sub CTA Text */}
            <p className="text-cyan-400 text-sm font-medium mb-2">
              {heroContent.ctaSubText}
            </p>
            <p className="text-slate-500 text-xs">
              {heroContent.disclaimer}
            </p>
          </div>

          {/* Right Content - Graph */}
          <div className="relative hidden md:block">
            <div className="relative bg-white/5 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-8 shadow-2xl">
              {/* E-E-A-T Graph */}
              <div className="flex justify-center">
                <img
                  src={EEATO_GRAPH_URL}
                  alt="E-E-A-T＋独自性 診断グラフ"
                  className="max-w-full h-auto"
                  style={{ maxHeight: '400px' }}
                />
              </div>
            </div>

            {/* Floating badges */}
            <div className="absolute -top-4 -right-4 bg-cyan-500 text-slate-900 px-4 py-2 rounded-full text-sm font-semibold shadow-lg">
              月2記事無料
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 border-2 border-slate-600 rounded-full flex justify-center pt-2">
          <div className="w-1.5 h-3 bg-slate-500 rounded-full animate-pulse" />
        </div>
      </div>
    </section>
  );
};

export default HeroSection;